import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import api, { errMsg } from "@/lib/api";

const EMPTY_BLOG = { title: "", slug: "", excerpt: "", body: "", published: false };

export default function AdminCms() {
  const [landing, setLanding] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [blogEdit, setBlogEdit] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadBlogs = () => api.get("/admin/blogs").then(({ data }) => setBlogs(data.blogs)).catch(() => {});
  useEffect(() => {
    api.get("/public/landing").then(({ data }) => setLanding(data)).catch(() => setLanding({}));
    loadBlogs();
  }, []);

  if (!landing) return <p className="text-muted-foreground">Loading…</p>;

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/landing", {
        hero_headline: landing.hero_headline,
        hero_subheading: landing.hero_subheading,
        ecosystem_note: landing.ecosystem_note,
        faqs: landing.faqs,
        banners: landing.banners || [],
        testimonials: landing.testimonials || [],
      });
      toast.success("Landing page content published");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const setListItem = (key, i, field, value) =>
    setLanding({ ...landing, [key]: (landing[key] || []).map((x, xi) => (xi === i ? { ...x, [field]: value } : x)) });
  const addListItem = (key, item) => setLanding({ ...landing, [key]: [...(landing[key] || []), item] });
  const removeListItem = (key, i) => setLanding({ ...landing, [key]: (landing[key] || []).filter((_, xi) => xi !== i) });

  const saveBlog = async () => {
    if (!blogEdit.title.trim()) {
      toast.error("Title is required");
      return;
    }
    try {
      if (blogEdit.id) await api.put(`/admin/blogs/${blogEdit.id}`, blogEdit);
      else await api.post("/admin/blogs", blogEdit);
      toast.success(blogEdit.published ? "Post published" : "Draft saved");
      setBlogEdit(null);
      loadBlogs();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const deleteBlog = async (id) => {
    try {
      await api.delete(`/admin/blogs/${id}`);
      toast.success("Post deleted");
      loadBlogs();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="max-w-3xl space-y-8" data-testid="admin-cms-page">
      <div className="bg-white border border-border/60 rounded-xl p-7 space-y-5">
        <h3 className="font-heading font-semibold text-[#0B1B3D]">Landing page</h3>
        <div>
          <Label>Hero headline</Label>
          <Input value={landing.hero_headline || ""} onChange={(e) => setLanding({ ...landing, hero_headline: e.target.value })} className="mt-1.5" data-testid="cms-headline" />
        </div>
        <div>
          <Label>Hero subheading</Label>
          <Textarea rows={3} value={landing.hero_subheading || ""} onChange={(e) => setLanding({ ...landing, hero_subheading: e.target.value })} className="mt-1.5" data-testid="cms-subheading" />
        </div>
        <div>
          <Label>Ecosystem note (replaces fake logos/stats until genuine data is verified)</Label>
          <Textarea rows={2} value={landing.ecosystem_note || ""} onChange={(e) => setLanding({ ...landing, ecosystem_note: e.target.value })} className="mt-1.5" data-testid="cms-ecosystem" />
        </div>
      </div>

      <div className="bg-white border border-border/60 rounded-xl p-7 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold text-[#0B1B3D]">Promo banners</h3>
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => addListItem("banners", { text: "", link: "", active: false })} data-testid="cms-add-banner">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add banner
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">The first active banner shows as a strip on the landing page.</p>
        {(landing.banners || []).map((b, i) => (
          <div key={i} className="border border-border/60 rounded-lg p-4 flex flex-wrap items-center gap-3" data-testid={`cms-banner-${i}`}>
            <Input placeholder="Banner text" value={b.text} onChange={(e) => setListItem("banners", i, "text", e.target.value)} className="flex-1 min-w-48" data-testid={`cms-banner-text-${i}`} />
            <Input placeholder="Link (optional, e.g. /jobs)" value={b.link || ""} onChange={(e) => setListItem("banners", i, "link", e.target.value)} className="w-48" data-testid={`cms-banner-link-${i}`} />
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <Switch checked={!!b.active} onCheckedChange={(v) => setListItem("banners", i, "active", v)} data-testid={`cms-banner-active-${i}`} /> Active
            </label>
            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => removeListItem("banners", i)} data-testid={`cms-banner-del-${i}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>

      <div className="bg-white border border-border/60 rounded-xl p-7 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold text-[#0B1B3D]">Testimonials</h3>
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => addListItem("testimonials", { name: "", role: "", quote: "" })} data-testid="cms-add-testimonial">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add testimonial
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">Publish only genuine, verifiable customer quotes — per platform policy.</p>
        {(landing.testimonials || []).map((t, i) => (
          <div key={i} className="border border-border/60 rounded-lg p-4 space-y-3" data-testid={`cms-testimonial-${i}`}>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Name" value={t.name} onChange={(e) => setListItem("testimonials", i, "name", e.target.value)} data-testid={`cms-testimonial-name-${i}`} />
              <Input placeholder="Role, Company" value={t.role} onChange={(e) => setListItem("testimonials", i, "role", e.target.value)} data-testid={`cms-testimonial-role-${i}`} />
            </div>
            <Textarea rows={2} placeholder="Quote" value={t.quote} onChange={(e) => setListItem("testimonials", i, "quote", e.target.value)} data-testid={`cms-testimonial-quote-${i}`} />
            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => removeListItem("testimonials", i)} data-testid={`cms-testimonial-del-${i}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>

      <div className="bg-white border border-border/60 rounded-xl p-7 space-y-4">
        <h3 className="font-heading font-semibold text-[#0B1B3D]">FAQs</h3>
        {(landing.faqs || []).map((f, i) => (
          <div key={i} className="border border-border/60 rounded-lg p-4 space-y-3">
            <Input value={f.q} onChange={(e) => setListItem("faqs", i, "q", e.target.value)} data-testid={`cms-faq-q-${i}`} />
            <Textarea rows={2} value={f.a} onChange={(e) => setListItem("faqs", i, "a", e.target.value)} data-testid={`cms-faq-a-${i}`} />
            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => removeListItem("faqs", i)} data-testid={`cms-faq-del-${i}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
        <Button variant="outline" size="sm" className="rounded-full" onClick={() => addListItem("faqs", { q: "", a: "" })} data-testid="cms-add-faq">
          Add FAQ
        </Button>
        <div className="pt-2">
          <Button onClick={save} disabled={busy} className="rounded-full bg-[#0B1B3D]" data-testid="cms-save-btn">
            {busy ? "Publishing…" : "Publish Changes"}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-border/60 rounded-xl p-7 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold text-[#0B1B3D]">Blog posts</h3>
          <Button size="sm" className="rounded-full bg-[#0B1B3D]" onClick={() => setBlogEdit({ ...EMPTY_BLOG })} data-testid="cms-add-blog">
            <Plus className="w-3.5 h-3.5 mr-1" /> New post
          </Button>
        </div>
        {blogs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No posts yet. Published posts appear at /blog and on the landing page.</p>
        ) : (
          <div className="space-y-2">
            {blogs.map((b) => (
              <div key={b.id} className="flex items-center justify-between border border-border/60 rounded-lg px-4 py-3" data-testid={`cms-blog-${b.id}`}>
                <div>
                  <p className="text-sm font-semibold text-[#0B1B3D]">{b.title}</p>
                  <p className="text-xs text-muted-foreground">/blog/{b.slug}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[11px] font-bold rounded-full px-2.5 py-0.5 ${b.published ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {b.published ? "Published" : "Draft"}
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => setBlogEdit({ ...b })} data-testid={`cms-blog-edit-${b.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteBlog(b.id)} data-testid={`cms-blog-del-${b.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!blogEdit} onOpenChange={() => setBlogEdit(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-2xl" data-testid="blog-edit-dialog">
          <DialogHeader><DialogTitle>{blogEdit?.id ? "Edit post" : "New post"}</DialogTitle></DialogHeader>
          {blogEdit && (
            <div className="space-y-4">
              <div><Label>Title</Label><Input value={blogEdit.title} onChange={(e) => setBlogEdit({ ...blogEdit, title: e.target.value })} className="mt-1.5" data-testid="blog-title" /></div>
              <div><Label>Slug (optional — auto-generated from title)</Label><Input value={blogEdit.slug} onChange={(e) => setBlogEdit({ ...blogEdit, slug: e.target.value })} className="mt-1.5" data-testid="blog-slug" /></div>
              <div><Label>Excerpt</Label><Textarea rows={2} value={blogEdit.excerpt} onChange={(e) => setBlogEdit({ ...blogEdit, excerpt: e.target.value })} className="mt-1.5" data-testid="blog-excerpt" /></div>
              <div><Label>Body</Label><Textarea rows={8} value={blogEdit.body} onChange={(e) => setBlogEdit({ ...blogEdit, body: e.target.value })} className="mt-1.5" data-testid="blog-body" /></div>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={blogEdit.published} onCheckedChange={(v) => setBlogEdit({ ...blogEdit, published: v })} data-testid="blog-published" /> Published (visible publicly)
              </label>
              <Button onClick={saveBlog} className="w-full rounded-full bg-[#0B1B3D]" data-testid="blog-save-btn">
                {blogEdit.id ? "Save Changes" : "Create Post"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
